/**
 * MeldungDetail — eine Meldung mit ihren Zahlen, dem Dokument und dem Beleg.
 *
 *   GET /api/meldungen/<id>          Zahlen (ohne die Bytes)
 *   GET /api/meldungen/<id>/dokument das Dokument (PDF oder Textfassung)
 *   GET /api/meldungen/<id>/csv      die Rohwerte
 *
 * 🔴 WAS HIER NICHT VERHANDELBAR IST:
 *
 * 1. Die Zahlen kommen unveraendert aus der API. Die Seite rechnet nichts nach —
 *    sie zeigt, was versandt wurde.
 * 2. Die WOERTER kommen aus `./meldungenText`: `art`, `status`, `dokument_typ`
 *    und `zahlen.gewichtslogik` sind ROHWERTE aus der API (Slug, deutsches
 *    Statuswort, `eml`, `geraetegewicht`), und dieselbe Abbildung traegt Liste
 *    UND Detail — vorher trug `verworfen` hier `warn` und in der Liste `plain`,
 *    derselbe Status in zwei Toenen.
 * 3. DREI LAGEN, DREI ANZEIGEN. Der Ladestreifen ist „laedt noch", die
 *    Fehlerflaeche ist „fehlgeschlagen", das Dokument-Panel traegt die Datei.
 *    Ein fehlgeschlagener Abruf darf nicht als Ladestreifen enden — das waere
 *    „ein Fehler sieht aus wie nichts da", genau die Unterscheidung, die diese
 *    Seite fuehrt. Die Fehlerflaeche traegt einen Weg heraus (`onRetry`), keinen
 *    `window.location.reload`.
 * 4. MOBIL KEIN RAHMEN (Regel 16 W1): `PdfInlineFrame` fuehrt „MOBILE" selbst als
 *    `@dontUseFor` — iOS Safari und mehrere Android-Browser zeigen von einem
 *    gerahmten PDF nur die erste Seite oder gar nichts, und der Rahmen kann das
 *    nicht bemerken. Unter `sm` steht deshalb der blanke Knopf.
 * 5. 🔴 ZWEI FORMEN VON `zahlen` (Spec §4B). VerpackG liefert
 *    `{land: {material: kg}}`, WEEE `{gesamt_kg, produkte:[{sku,name,kg}],
 *    gewichtslogik}`. Die Weiche ist `m.art` — dieselbe Stelle, an der
 *    `artText` haengt. Ohne sie rendert die Land×Material-Tabelle Zeilen mit
 *    `land="produkte"`, `material="sku"` und **„NaN"**: sie sieht nicht leer
 *    aus, sie sieht KAPUTT aus (JS-Semantik: `Object.keys` auf einem Array gibt
 *    die Indizes, auf einer Zahl `[]`, und `Number(undefined)` ist `NaN`).
 * 6. 🔴 NUR EIN `pdf` GEHT IN DEN RAHMEN. `PdfInlineFrame` ist laut eigenem Kopf
 *    „the ONE place a PDF is mounted inline in this app" und traegt die Woerter
 *    „PDF preview loaded" / „Download PDF" in seinem Inneren. Ein `eml`
 *    (`message/rfc822`, Spec §4A) zeichnet kein Browser — dort gehoert der
 *    Auslieferungsweg hin (Link/Download), nicht der Rahmen. Das Wort kommt aus
 *    `dokumentText`, nie aus einem Literal: der `title` des Rahmens ist der
 *    zugaengliche Name des Bauteils, keine Kosmetik.
 * 7. 🔴 DER CSV-WEG HAENGT AN `csv_text`, NICHT AN DER ART. WEEE schreibt
 *    `csv_text = NULL` (Spec §4B) — der Knopf erscheint dort nicht, weil
 *    `csv_bytes` 404 antwortet und die Flaeche sonst „The file could not be
 *    loaded." fuer eine Datei meldete, die es NIE gab. Die Liste fuehrt die
 *    Spalte dafuer in ihrer SELECT-Liste (`ablage.liste`); beide Flaechen
 *    muessen dieselbe Bedingung tragen, sonst bleibt die eine die Sackgasse,
 *    die die andere geschlossen hat.
 */
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'sonner'
import { useParams } from 'react-router-dom'
import { Button, Link, Skeleton, Stack, Table, TableBody, TableCell, TableHead,
         TableRow, Typography, useMediaQuery, useTheme } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import Pill from '../components/common/Pill'
import EmptyState from '../components/common/EmptyState'
import PdfInlineFrame from '../components/common/PdfInlineFrame'
import TableViewport from '../components/common/TableViewport'
import ViewHeader from '../components/ViewHeader'
import { KEIN_WERT, artText, datumDe, dokumentText, gewichtslogikText, statusPill }
         from './meldungenText'
import { SP } from '../theme/tokens'

// 🔴 Die GESCHUETZTE Hausform — nicht die nackte. Beide gibt es im Baum
//    (42 geschuetzt, 82 nackt); nur diese funktioniert in BEIDEN Lagen: mit
//    gesetzter Variable absolut, OHNE sie leer → relativ → CRA-Proxy. Der
//    nackte Ausdruck ergibt ohne `frontend/.env.local` `undefined/api/…`
//    (gemessen: dieser Worktree hat keine solche Datei). Vorlage:
//    `frontend/src/ProductDetail.jsx:69`.
const API = (process.env.REACT_APP_API_BASE_URL || '')

// 🔴 Die Dateien liegen HINTER dem JWT-Tor. Ein nackter `href` oder ein
//    `<object data="/api/...">` kann den Authorization-Header NICHT setzen —
//    eine Browser-Navigation und ein `<object>` tragen ihn nicht, und es gibt
//    keinen Cookie- oder Query-Token-Weg (`backend/global_auth_gate.py` fuehrt
//    den Pfad nicht in `_PUBLIC_PREFIXES`; `backend/auth_utils.py:179-184`
//    liest ausschliesslich den Header). In dev ist das UNSICHTBAR, weil
//    `DEV_DISABLE_AUTH` das Tor oeffnet — in prod liefen PDF, CSV und das
//    Dokument-Panel in ein 401.
//
//    Der Weg ist der des Repos: axios traegt den Header
//    (`frontend/src/index.js:59-71`), also als **Blob** holen und ueber eine
//    Objekt-URL oeffnen. Vorbild: `frontend/src/pages/invoicing/KorrekturDialog.jsx`.
async function holeAlsBlob(pfad) {
  const { data } = await axios.get(pfad, { responseType: 'blob' })
  return URL.createObjectURL(data)
}

async function lade(pfad, dateiname) {
  // 🔴 Die Ablehnung darf nicht unbehandelt bleiben — sonst sieht der Nutzer
  //    NICHTS, wenn der Abruf scheitert (kein Fehler, keine Meldung). Und der
  //    Weg heraus ist der des Hauses (`sonner`), kein `window.alert`: der
  //    Katalog fuehrt `window.alert` als `dontUseFor` des Dialogmusters
  //    (`frontend/src/design/catalog.jsx:232`).
  try {
    const url = await holeAlsBlob(pfad)
    const a = document.createElement('a')
    a.href = url
    a.download = dateiname
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch {
    toast.error('The file could not be loaded.')
  }
}

export default function MeldungDetail() {
  const { id } = useParams()
  const theme = useTheme()
  // 🔴 Die Weiche gehoert dem AUFRUFER, nicht dem Bauteil: `PdfInlineFrame`
  //    schaltet sich bewusst nicht selbst ab (sein JSDoc), weil ein stiller
  //    Selbstausschluss schwerer zu finden waere als diese Zeile.
  const schmal = useMediaQuery(theme.breakpoints.down('sm'))
  const [m, setM] = useState(null)
  // 🔴 `null` = kein Fehler, `'fehlt'` = 404 (gibt es nicht), `'abruf'` = jeder
  //    andere Fehlschlag. Die zwei Lagen tragen ZWEI Anzeigen (siehe unten): nur
  //    der 404 heisst „does not exist", und nur der andere braucht einen Weg
  //    heraus.
  const [fehler, setFehler] = useState(null)
  const [dokumentUrl, setDokumentUrl] = useState(null)
  const [dokumentFehler, setDokumentFehler] = useState(false)
  const [dokumentLauf, setDokumentLauf] = useState(0)
  // Der Weg heraus fuer einen GESCHEITERTEN Abruf (kein 404) — Bauform wie
  // `Meldungen.jsx` (`lauf`).
  const [lauf, setLauf] = useState(0)

  useEffect(() => {
    let ab = false
    // 🔴 BEIDE Zustaende zurueck beim Wechsel der Kennung. Ohne das steht der
    //    Fehler der ALTEN Meldung ueber der neuen: wer `/declarations/999` (404)
    //    aufruft und von dort auf eine gueltige Meldung geht, laese weiter
    //    „This declaration does not exist." — fuer eine, die es gibt. Und ohne
    //    `setM(null)` stuenden die ZAHLEN der alten Meldung unter der neuen
    //    Adresse, bis die Antwort da ist. Die Schwesterliste macht es an
    //    derselben Stelle so (`Meldungen.jsx`, `setFehler(false)` im Effekt).
    setFehler(null)
    setM(null)
    axios.get(`${API}/api/meldungen/${id}`)
      .then((r) => { if (!ab) setM(r.data) })
      .catch((e) => {
        if (ab) return
        // 🔴 NUR der 404 heisst „gibt es nicht". Jeder andere Fehlschlag (500,
        //    Netzfehler) ist ein ABRUFfehler — ein anderer Wortlaut, und mit
        //    einem Weg heraus.
        setFehler(e?.response?.status === 404 ? 'fehlt' : 'abruf')
      })
    return () => { ab = true }
  }, [id, lauf])

  // 🔴 Dasselbe Tor wie bei den Links: der Rahmen bekommt keine Adresse auf
  //    `/api/...`, sondern die Objekt-URL des geholten Blobs. `PdfInlineFrame`
  //    nimmt eine Objekt-URL (so wie `KorrekturDialog.jsx:718` es tut) — er
  //    probt nur Wurzelpfade, eine `blob:`-Adresse mountet er ungeprueft.
  //
  //    🔴 Der `catch` ist kein Zierrat. Die Testumgebung ist der Regelfall, nicht
  //    die Ausnahme: jsdom 16 kennt `URL.createObjectURL` NICHT (gemessen
  //    2026-10-13), der Aufruf wirft dort also — und landet damit im Fehler-,
  //    nicht im Ladezustand.
  useEffect(() => {
    if (!m) return undefined
    let ab = false
    let erzeugt = null
    // Ein Wiederholungslauf beginnt im LADEZUSTAND, nicht auf der alten Flaeche.
    setDokumentUrl(null)
    setDokumentFehler(false)
    holeAlsBlob(`${API}/api/meldungen/${m.id}/dokument`)
      .then((url) => {
        if (ab) { URL.revokeObjectURL(url); return }
        erzeugt = url
        setDokumentUrl(url)
      })
      .catch(() => { if (!ab) setDokumentFehler(true) })
    return () => { ab = true; if (erzeugt) URL.revokeObjectURL(erzeugt) }
  }, [m, dokumentLauf])

  if (fehler) {
    return (
      <PageContainer>
        {/* 🔴 ZWEI LAGEN, ZWEI ANZEIGEN — der Zweig ist durch JEDEN Fehlschlag
            von `GET /api/meldungen/<id>` erreichbar (404, 500, Netzfehler), und
            nur EINE davon bedeutet „gibt es nicht". Wer sie zusammenwirft,
            behauptet bei einem 500, die Meldung sei verworfen. */}
        {fehler === 'fehlt' ? (
          // 404: eine verworfene Fassung ist ein ENDzustand, und ein Knopf
          // fuehrte in dieselbe 404 — dieselbe Begruendung, aus der
          // `PdfInlineFrame` seinem `missing`-Zustand keinen „Open file"-Knopf
          // gibt.
          <EmptyState reason="error" message="This declaration does not exist."
                      detail="The version may have been discarded." />
        ) : (
          // Jeder ANDERE Fehlschlag ist KEIN Endzustand: er nennt den Fehlschlag
          // (nicht „does not exist") und traegt den Weg heraus (`onRetry`, wie
          // `Meldungen.jsx`). Der Wortlaut ist der des Dokument-Panels darunter —
          // dieselbe Lage, dieselben zwei Zeilen.
          <EmptyState reason="error" message="The declaration could not be loaded."
                      detail="The request failed."
                      onRetry={() => setLauf((n) => n + 1)} />
        )}
      </PageContainer>
    )
  }
  if (m === null) {
    // 🔴 Ein Geruest in der Form der geladenen Seite (drei Panels), nicht ein
    //    200-px-Balken ohne Rolle: es meldet den Ladezustand ueber
    //    `role="status"` + `aria-label` + `aria-busy` (anti-patterns §11).
    return (
      <PageContainer boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
        <Stack role="status" aria-label="Loading the declaration" aria-busy="true" spacing={SP.section}>
          <Stack spacing={SP.tight}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="text" width="25%" />
          </Stack>
          <Skeleton variant="rectangular" height={96} />
          <Skeleton variant="rectangular" height={160} />
          <Skeleton variant="rectangular" height={240} />
        </Stack>
      </PageContainer>
    )
  }

  const p = statusPill(m.status)

  // 🔴 EINE Normalisierung, EIN Wort. Die Spalte ist `NOT NULL DEFAULT 'pdf'`
  //    (Migration 397), der Rueckfall deckt also nur eine Nutzlast, die aelter
  //    ist als die Spalte. Er stand vorher zweimal im Baum (nur fuer den
  //    Dateinamen) — jetzt einmal, weil Beschriftung, Rahmen-Titel und
  //    Auslieferungsweg dasselbe Wort brauchen.
  const typ = m.dokument_typ || 'pdf'
  const dateiname = `${m.art}-${m.periode}-f${m.fassung}.${typ}`

  // 🔴 DIE WEICHE ZWISCHEN DEN ZWEI FORMEN VON `zahlen` (Spec §4B) — sie haengt
  //    an `art`, nicht an der Gestalt der Nutzlast. Eine Probe auf „hat es
  //    `produkte`?" waere die zweite Wahrheit neben der API: eine VerpackG-
  //    Meldung mit einem Feld dieses Namens fiele in den falschen Zweig.
  const istWeee = m.art === 'weee_monatsmeldung'

  // Die Land×Material-Form (VerpackG) — unveraendert.
  const zeilen = []
  if (!istWeee) {
    Object.keys(m.zahlen || {}).sort().forEach((land) => {
      Object.keys(m.zahlen[land]).sort().forEach((material) => {
        zeilen.push({ land, material, kg: m.zahlen[land][material] })
      })
    })
  }

  // Die Produktform (WEEE). `Array.isArray` statt `!== undefined`: eine Nutzlast
  // ohne `produkte` darf die Seite nicht an `.map` reissen.
  const produkte = istWeee && Array.isArray(m.zahlen?.produkte) ? m.zahlen.produkte : []

  // 🔴 `typeof` VOR `Number`, und das ist keine Stilfrage: `Number(null)` ist
  //    **0**, nicht `NaN`. Eine Fassung, die nur `Number.isFinite(Number(x))`
  //    prueft, schreibt fuer ein `null` „0,000" — genau die erfundene Zahl, die
  //    der Kommentar darunter ausschliesst (Regel 15 H-2). EIN Praedikat fuer
  //    die Summe UND die Zellen, damit die zwei nicht auseinanderlaufen.
  const istZahl = (wert) => typeof wert === 'number' && Number.isFinite(wert)

  // 🔴 Die SUMME ist die eine Zahl, die als SATZ auf der Flaeche steht — sie
  //    darf nicht „NaN" heissen, wenn der Wert fehlt, und nicht „0,000", wenn er
  //    nicht gemeldet wurde. Gemessen, nicht geraten:
  //    `backend/weee_report.py::weee_zahlen` setzt `gesamt_kg` immer; eine
  //    Nutzlast ohne das Feld kommt also nur an einer aelteren Fassung vorbei —
  //    und dann ist „nicht gemeldet" die ehrliche Auskunft statt einer Zahl.
  const hatSumme = istZahl(m.zahlen?.gesamt_kg)

  // `1.156,809` — dieselbe Ziffernform wie bisher, an EINER Stelle statt in
  // jeder Tabelle. Die Ziffernformate bleiben deutsch (Kopf dieser Datei).
  // 🔴 Und ein Wert, der keine Zahl ist, ergibt den Hausstrich und nicht „NaN"
  //    (Regel 22 Nr. 3: „Unbekannt ist nie leer"). Das ist derselbe Defekt, den
  //    Spec §4B an der Land×Material-Tabelle bemaengelt hat — er sitzt hier in
  //    der Formatierung, nicht in der Weiche.
  const kgDe = (wert) => (istZahl(wert)
    ? wert.toLocaleString('de-DE', { minimumFractionDigits: 3 })
    : KEIN_WERT)

  return (
    // 🔴 Derselbe Abstand wie in `Meldungen.jsx`: ohne ihn stossen die drei
    // Panels mit ihren Rahmen aneinander (`Panel` traegt kein `mb`/`mt`).
    <PageContainer boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <ViewHeader title={`${artText(m.art)} ${m.periode}`}
                  subtitle={`Version ${m.fassung} · ${m.empfaenger}`}
                  backLink={{ label: 'Back to declarations', href: '/declarations' }} />

      <Panel component="section" aria-labelledby="md-status">
        <Typography id="md-status" variant="subtitle1" component="h2" sx={{ mb: SP.tight }}>
          Status
        </Typography>
        <Stack direction="row" spacing={SP.tight} alignItems="center" flexWrap="wrap" useFlexGap>
          <Pill tone={p.tone} bordered>{p.text}</Pill>
          <Typography variant="body2">Sent on {datumDe(m.versandt_am)}</Typography>
          {/* 🔴 Die Beschriftung ist das WORT der Form (`dokumentText`), kein
              Literal „PDF": fuer eine WEEE-Meldung liegen hier die Bytes einer
              Mail. Der Auslieferungsweg ist in beiden Faellen derselbe
              (Blob + `download`), nur der Name der Datei kommt aus dem Typ. */}
          <Link component="button" type="button"
                onClick={() => lade(`${API}/api/meldungen/${m.id}/dokument`, dateiname)}>{dokumentText(typ)}</Link>
          {/* 🔴 Der CSV-Weg erscheint NUR, wenn es eine CSV gibt. `csv_text` ist
              bei WEEE leer (Spec §4B, `sync/weee_report.py`), und `csv_bytes`
              antwortet dort 404 — ein Knopf, der dort hinfuehrt, meldete „The
              file could not be loaded." fuer eine Datei, die es NIE gab. Das ist
              dieselbe Unterscheidung, aus der der 404-Zweig oben bewusst KEINEN
              Wiederholen-Knopf traegt („ein Knopf fuehrte in dieselbe 404").
              🔴 Die Bedingung fragt die DATEN, nicht die Art: „WEEE hat keine
              CSV" waere eine zweite Wahrheit neben der API — eine WEEE-Meldung
              mit `csv_text` soll ihren Knopf bekommen. */}
          {m.csv_text && (
            <Link component="button" type="button"
                  onClick={() => lade(`${API}/api/meldungen/${m.id}/csv`,
                                     `${m.art}-${m.periode}-f${m.fassung}.csv`)}>Raw values (CSV)</Link>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: SP.tight }}>
          The proof is the document's SHA-256: <code>{m.dokument_sha256}</code>
        </Typography>
      </Panel>

      <Panel component="section" aria-labelledby="md-zahlen">
        {/* 🔴 Auch die UEBERSCHRIFT haengt an der Form: eine WEEE-Tabelle hat
            keine Spalte „Country" — „by country and material" waere eine
            Beschreibung, die ihren Gegenstand nicht trifft. */}
        <Typography id="md-zahlen" variant="subtitle1" component="h2" sx={{ mb: SP.tight }}>
          {istWeee ? 'Quantity by product' : 'Quantity by country and material'}
        </Typography>
        {/* 🔴 Der Rollbehaelter (Regel 16 W5): `theme.js` setzt keine
            `MuiTable`-Ueberlaufregel. `Material` traegt Werte wie
            „Papier/Pappe/Karton" — ohne Rollbehaelter stauchen sie die Spalte.
            `pad="card"` bleibt: dieses Panel ist ein Abschnitt MIT Ueberschrift,
            kein randloses Daten-Panel wie das Register der Liste. */}
        {istWeee ? (
          // ── WEEE: je SKU eine Zeile, kein Land und kein Material (Spec §4B) ──
          produkte.length === 0 ? (
            // 🔴 Kein Fehler, sondern ein ZUSTAND: ein Monat ohne Umsatz wird
            //    gemeldet (Owner-Entscheid 2026-10-15, Spec §4D2) und traegt dann
            //    0 kg. Eine leere Tabelle waere hier „nichts da" statt „null".
            <EmptyState reason="no-data"
                        message="No product lines in this declaration."
                        detail="A month without sales is reported with 0 kg." />
          ) : (
            <TableViewport footerPadPx={0}>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>SKU</TableCell><TableCell>Product</TableCell><TableCell align="right">kg</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {produkte.map((z) => (
                    <TableRow key={z.sku}>
                      <TableCell>{z.sku}</TableCell>
                      <TableCell>{z.name}</TableCell>
                      <TableCell align="right">{kgDe(z.kg)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableViewport>
          )
        ) : zeilen.length === 0 ? (
          // 🔴 Derselbe Zustand wie bei WEEE, aus demselben Grund — und GEMESSEN
          //    erreichbar: ohne Verkaeufe ist `gesamt_je_land` `{}`
          //    (`sync/verpackg_report.py:353`; `backend/tests/test_meldung_lauf.py:78`
          //    haelt es fest). Eine Tabelle mit Kopf und null Zeilen saehe aus wie
          //    „nichts da". 🔴 Und KEINE „0 kg": die Zahlen sind LEER, nicht null —
          //    eine 0 waere hier ein erfundener Wert.
          <EmptyState reason="no-data"
                      message="No quantities in this declaration."
                      detail="A year without sales is reported with empty figures." />
        ) : (
          <TableViewport footerPadPx={0}>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Country</TableCell><TableCell>Material</TableCell><TableCell align="right">kg</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {zeilen.map((z) => (
                  <TableRow key={`${z.land}-${z.material}`}>
                    <TableCell>{z.land}</TableCell>
                    <TableCell>{z.material}</TableCell>
                    <TableCell align="right">{kgDe(z.kg)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableViewport>
        )}
        {/* 🔴 Die SUMME kommt aus `gesamt_kg` und wird NICHT aus den Zeilen
            gerechnet: die Seite rechnet nichts nach (Punkt 1 im Kopf). Und die
            Gewichtsgrundlage steht daneben, weil dieselbe Meldung mit
            „geraetegewicht" und mit „mit_verpackung" VERSCHIEDENE Kilogramm
            bedeutet — eine Zahl ohne ihre Grundlage ist eine halbe Auskunft. */}
        {istWeee && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: SP.tight }}>
            {/* 🔴 Ein SATZ mit Platzhalter liest sich wie ein Wert — deshalb hier
                ein benannter Zustand („Total not reported") statt `Total — kg`,
                waehrend eine ZELLE den Hausstrich traegt (`kgDe`). */}
            {hatSumme ? `Total ${kgDe(m.zahlen.gesamt_kg)} kg` : 'Total not reported'}
            {' · '}Weight basis: {gewichtslogikText(m.zahlen?.gewichtslogik)}
          </Typography>
        )}
      </Panel>

      <Panel component="section" aria-labelledby="md-dokument">
        <Typography id="md-dokument" variant="subtitle1" component="h2" sx={{ mb: SP.tight }}>
          The document
        </Typography>
        {dokumentUrl ? (
          // 🔴 DREI WEGE, EIN KRITERIUM: kann ein Browser diese Bytes im Rahmen
          //    zeigen? Die Reihenfolge ist tragend — die Form entscheidet VOR
          //    dem Viewport, sonst bekaeme ein `eml` auf schmalen Geraeten den
          //    PDF-Knopf („Open PDF") und auf breiten den Rahmen.
          typ !== 'pdf' ? (
            // Ein `eml` ist `message/rfc822`: kein Browser zeichnet das, und
            // `PdfInlineFrame` truege fuer die Bytes einer Mail ausserdem seine
            // eigenen Woerter („PDF preview loaded", „Download PDF") mit sich.
            // Hier ist der Auslieferungsweg der ehrliche.
            <>
              {/* 🔴 Der SATZ, der sagt WARUM kein Vorschaurahmen da ist. Die
                  Hausform dafuer ist die Ueberschrift des `PDF_DOWNLOAD`-Zweigs
                  in `PdfInlineFrame` („This PDF can't be shown here — it opens as
                  a download."): ein blanker Knopf laesst den Nutzer fragen, wo
                  die Vorschau bleibt. Beim schmalen PDF-Knopf fehlt der Satz mit
                  Grund — dort ist die Ursache der VIEWPORT (der Nutzer weiss, dass
                  ein PDF sonst vorschaut), hier ist sie die FORM: eine `.eml`
                  kann NIE vorschauen. */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: SP.tight }}>
                This email file can't be shown here — it opens as a download.
              </Typography>
              {/* 🔴 `download` setzt den Namen aus DIESER Flaeche (`dateiname`),
                  nicht aus `Content-Disposition`: die Bytes kommen als Blob ueber
                  `axios`, der Server ist an dieser Stelle gar nicht beteiligt.
                  🔴 Und das Symbol ist das der HANDLUNG: der Knopf laedt herunter,
                  er oeffnet nichts nebenan (Hausform: `PdfInlineFrame`,
                  `InvoiceDetail`). */}
              <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} component="a"
                      href={dokumentUrl} download={dateiname}
                      sx={{ alignSelf: 'flex-start' }}>
                Download {dokumentText(typ)}
              </Button>
            </>
          ) : schmal ? (
            // Die Hausform fuer ein PDF ohne Rahmen (`MediaDetailDrawer.mobilePdf`).
            <Button variant="outlined" startIcon={<OpenInNewIcon />} component="a"
                    href={dokumentUrl} target="_blank" rel="noopener noreferrer"
                    sx={{ alignSelf: 'flex-start' }}>
              Open {dokumentText(typ)}
            </Button>
          ) : (
            // 🔴 Das geteilte Bauteil statt eines nachgebauten `<object>`: es ist
            //    laut eigenem Kopf „the ONE place a PDF is mounted inline in this
            //    app", traegt fuenf ehrliche Zustaende, eine stabile
            //    `role="status"`-Region und im Erfolgszustand einen
            //    Download-Link — „Rendering the PDF is not the same as giving the
            //    user the file". Sein `title` ist der zugaengliche Name — und er
            //    kommt aus `dokumentText`, nicht aus einem Literal.
            <PdfInlineFrame url={dokumentUrl} title={`Declaration as ${dokumentText(typ)}`} />
          )
        ) : dokumentFehler ? (
          <EmptyState reason="error" message="The document could not be loaded."
                      detail="The request failed."
                      onRetry={() => setDokumentLauf((n) => n + 1)} />
        ) : (
          <Skeleton variant="rectangular" height={200} role="status"
                    aria-label="Loading the document" aria-busy="true" />
        )}
      </Panel>
    </PageContainer>
  )
}
